
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { buildSpecLibraryTable } from "./MainTabs/buildSpecLibraryTable";
import {
  fetchAllRevsForProject,
  fetchExtractPnid,
  handleExtractLineListOnly,
  postInstrumentationRev,
  fetchInstrumentationRev,
  updateInstrumentationRev,
  getInstrumentationPlantProjects,
  getAllData,
  getProjectById,
} from "./api";
import { useParams } from "react-router-dom";
import { useGeneralContext } from "../../AllEquipments/CommonFeatures/SidePanel/GeneralContext";
import { notifyProjectSaved } from "../../../../components/Drawers/TreeViewDrawer/projectSavedEvent";
import {
  isPlainObject,
  createEmptyDataSheetsStore,
  createEmptyPnidRevision,
  normalizeDataSheetsStore,
  normalizePnidRevisionData,
  buildNormalizedPnidData,
} from "./pnidNormalizers";
import { mergeBulkDataWithDerivation, mergeBulkData } from "./MainTabs/PnID/BulkList/bulkListHelpers";
import {
  buildDataSheetForSourceRow,
  buildRowDataSheet,
  consolidateDataSheets,
} from "./MainTabs/PnID/DataSheets/dataSheetHelpers";
import { secondAxios } from "../../../../api/axios";
import { mergeSpecRowsByIdentity } from "./MainTabs/Spec/specRow";
import { extractInstrumentationRevisionData } from "./instrumentationRevisions";
import { resolvePlantSpec } from "./specSourceLink";
// Stamped on every payload this module saves - Piping saves as "piping",
// Structural as "structural".
const INSTRUMENTATION_TYPE = "instrumentation";

function migrateTagNumbersList(value) {
  if (!Array.isArray(value)) return [];
  if (value.length === 0) return [];

  if (typeof value[0] === "object" && value[0] !== null) {
    return value
      .filter((g) => g && typeof g === "object")
      .map((g) => ({
        lineNumber: String(g.lineNumber ?? "").trim(),
        tagNumbers: Array.isArray(g.tagNumbers)
          ? g.tagNumbers.map((t) => String(t ?? "").trim()).filter(Boolean)
          : [],
      }));
  }

  const tagNumbers = value.map((t) => String(t ?? "").trim()).filter(Boolean);
  return tagNumbers.length ? [{ lineNumber: "", tagNumbers }] : [];
}

/**
 * @typedef {Object} DrawingFile
 * @property {string} path
 * @property {string} name
 * @property {number} size
 * @property {string} modified
 */

/**
 * @typedef {Object} InstrumentationContextValue
 * @property {*} specTable
 * @property {Function} setSpecTable
 * @property {*} fullSpecTable
 * @property {Function} setFullSpecTable
 * @property {*} partLibTable
 * @property {Function} setPartLibTable
 * @property {*} estimationTable
 * @property {Function} setEstimationTable
 * @property {*} trimListTable
 * @property {Function} setTrimListTable
 * @property {Object} allTables
 * @property {Function} initializeAllTables
 * @property {{label: string, spec: string, rev: string | number}[]} selectedSpecs
 * @property {(newSelectedSpec: {label: string, spec: string, rev: string | number}[]) => void} addSlectedSpec
 * @property {(newSelectedSpec: {label: string, spec: string, rev: string | number}[]) => void} removeSelectedSpec
 * @property {(newSelectedSpec: {label: string, spec: string, rev: string | number}[]) => Object} getSelectedSpecTable
 * @property {Function} saveInstrumentationData
 * @property {Function} setSelectedSpecs
 * @property {(pnidPath: string) => Promise<void>} extractPnid
 * @property {boolean} isFetchingPnid
 * @property {DrawingFile[]} pnidDrawings
 * @property {Dispatch<SetStateAction<DrawingFile[]>>} setPnidDrawings
 */

/** @type {React.Context<InstrumentationContextValue | undefined>} */
const InstrumentationContext = createContext(undefined);

export const InstrumentationProvider = ({ children }) => {
  const {
    selectedModelingRevision,
    setSelectedModelingRevision,
    fastenersTable,
    setFastenersTable,
    modelingData,
    setModelingData,
  } = useGeneralContext();
  // const [UID_array, setUID_array] = useState([]);
  const [allTables, setAllTables] = useState({});
  const [specTable, setSpecTable] = useState([]);
  const [fullSpecTable, setFullSpecTable] = useState([]); // NEW: keeps duplicates for searching
  // specTable/fullSpecTable are built from two sources, as in the Software:
  // the SQLite piping_spec_* catalogue, and rows the user authored in the
  // Software's Spec Helper, which live only in the project payload.
  const [dbSpecRows, setDbSpecRows] = useState([]);
  const [customSpecRows, setCustomSpecRows] = useState([]);
  // Per-spec Spec Helper rule configs. The web has no editor for these, but
  // they must still be carried through a save or it would erase them.
  const [specHelperConfigs, setSpecHelperConfigs] = useState({});
  const [selectedSpecs, setSelectedSpecs] = useState([]);
  const [partLibTable, setPartLibTable] = useState({});
  const [estimationTable, setEstimationTable] = useState({});
  // Id of the project whose data is currently loaded (null while a load is
  // in flight) - see loadInstrumentationData.
  const [loadedProjectId, setLoadedProjectId] = useState(null);

  // A revision is only “confirmed” once the user picks it from the Rev.
  // dropdown in the header. Until then there is no revision to read, so loading
  // a project shows its revision list without showing any of its data — same
  // flow as Pressure Vessel.
  const [selectedEstimationRevision, setSelectedEstimationRevision] =
    useState("");
  const [confirmedEstimationRevision, setConfirmedEstimationRevision] =
    useState(null);

  const [pnidData, setPnidData] = useState({});
  const [selectedPnidRevision, setSelectedPnidRevision] = useState("");
  const [pnidProjectPath, setPnidProjectPath] = useState("");
  const [pnidRevisionFolders, setPnidRevisionFolders] = useState([]);
  const [pnidFolderTree, setPnidFolderTree] = useState(null);

  const [validationObject, setValidationObject] = useState({
    left: null,
    right: null,
  });

  const FRONTEND_UID_ARRAY = [
    "B06X3A-3",
    "B06X3A-4",
    "B06X3A-5",
    "UID-001",
    "UID-002",
    "UID-003",
  ];
  const [revision, setRevision] = useState(undefined);
  const [isFetchingPnid, setIsFetchingPnid] = useState(false);
  const [pnidDrawings, setPnidDrawings] = useState([]);
  const [pdcAttachItems, setPdcAttachItems] = useState([]);
  const [allUiData, setAllUiData] = useState({});
  const [pageViews, setPageViews] = useState({});
  const [pages, setPages] = useState([]);
  const [projectPath, setProjectPath] = useState(null);
  const [hookupsTable, setHookupsTable] = useState([]);
  const [tagNumbersList, setTagNumbersList] = useState([]);
  const [selectedLineNumber, setSelectedLineNumber] = useState("");
  const [allCombinationTable, setAllCombinationTable] = useState([]);
  const [specLibraryTable, setSpecLibraryTable] = useState([]);
  const [isBuildingSpecLibrary, setIsBuildingSpecLibrary] = useState(false);
  const [UID_array, setUID_array] = useState(FRONTEND_UID_ARRAY);
  const SelectedParts = useMemo(() => {
    const byTab = {};
    allCombinationTable.forEach((row) => {
      if (row.select && row.TabName) {
        if (!byTab[row.TabName]) byTab[row.TabName] = [];
        byTab[row.TabName].push(row);
      }
    });
    return byTab;
  }, [allCombinationTable]);
 const[selectedProjectId, setSelectedProjectId] = useState(null);
  const [totalSummary, setTotalSummary] = useState([
    { name: "Total Materials Cost", value: "", unit: "$", rate: "", total: "" },
    { name: "Total Man Hours", value: "", unit: "hr", rate: "", total: "" },
    { name: "Galvanized Cost", value: "", unit: "Ft²", rate: "", total: "" },
    { name: "Painting Cost", value: "", unit: "Ft²", rate: "", total: "" },
    { name: "Fire Proof Cost", value: "", unit: "Ft²", rate: "", total: "" },
    { name: "Miscellaneous", value: "", unit: "LS", rate: "1", total: "" },
  ]);

  const getCurrentRevisionData = useCallback(() => {
    if (!selectedPnidRevision) {
      return createEmptyPnidRevision();
    }

    return normalizePnidRevisionData(pnidData[selectedPnidRevision]);
  }, [pnidData, selectedPnidRevision]);

  const trimListTable = getCurrentRevisionData().trimListTable;
  const bulkListTable = getCurrentRevisionData().bulkListTable;
  const valveListTable = getCurrentRevisionData().valveListTable;
  const instrumentIndexTable = getCurrentRevisionData().instrumentIndexTable;
  const lineListTable = getCurrentRevisionData().lineListTable;
  const equipmentListTable = getCurrentRevisionData().equipmentListTable;
  const titleBlockData = getCurrentRevisionData().titleBlockData;
  const dataSheetsStore = getCurrentRevisionData().dataSheetsStore;

  const initializeRevisionIfNeeded = useCallback((revisionKey) => {
    if (!revisionKey) return;

    setPnidData((prev) => {
      if (prev[revisionKey]) return prev;
      return {
        ...prev,
        [revisionKey]: createEmptyPnidRevision(),
      };
    });
  }, []);

  const clearSelectedPnidRevision = useCallback(() => {
    if (!selectedPnidRevision) return;

    setPnidData((prev) => ({
      ...prev,
      [selectedPnidRevision]: createEmptyPnidRevision(),
    }));
  }, [selectedPnidRevision]);

  const setTrimListTable = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          trimListTable:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.trimListTable || [])
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setBulkListTable = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          bulkListTable:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.bulkListTable || [])
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setValveListTable = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          valveListTable:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.valveListTable || [])
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setInstrumentIndexTable = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          instrumentIndexTable:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.instrumentIndexTable || [])
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setLineListTable = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          lineListTable:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.lineListTable || [])
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setEquipmentListTable = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          equipmentListTable:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.equipmentListTable || [])
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setDataSheetsStore = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          dataSheetsStore:
            typeof newData === "function"
              ? newData(
                prev[selectedPnidRevision]?.dataSheetsStore ||
                createEmptyPnidRevision().dataSheetsStore,
              )
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  const setTitleBlockData = useCallback(
    (newData) => {
      if (!selectedPnidRevision) return;

      setPnidData((prev) => ({
        ...prev,
        [selectedPnidRevision]: {
          ...(prev[selectedPnidRevision] || createEmptyPnidRevision()),
          titleBlockData:
            typeof newData === "function"
              ? newData(prev[selectedPnidRevision]?.titleBlockData || {})
              : newData,
        },
      }));
    },
    [selectedPnidRevision],
  );

  // ────────────────────────────────────────────────────────────────────────────

  function processSpecData(rows) {
    // Remove duplicates
    const seen = new Map();
    for (const row of rows) {
      const key = `${row.Spec}|${row.Components}|${row.Size}|${row.Material}|${row.Ends}|${row["Schedule/Rating"]}|${row.Rev}`;
      if (!seen.has(key)) {
        seen.set(key, row);
      }
    }
    const uniqueRows = Array.from(seen.values());

    // Sort alphabetically by Components, then by Size
    return uniqueRows.sort((a, b) => {
      const compCompare = (a.Components || "").localeCompare(
        b.Components || "",
      );
      if (compCompare !== 0) return compCompare;

      // Natural sort for sizes
      const sizeA = parseFloat(a.Size) || 0;
      const sizeB = parseFloat(b.Size) || 0;
      if (sizeA !== sizeB) return sizeA - sizeB;

      return (a.Size || "").localeCompare(b.Size || "");
    });
  }

  const [clientName, setClientName] = useState("");

  const initializeAllTables = useCallback(async (clientNameArg = "DEMO") => {
  const safeClientName = String(clientNameArg || "DEMO")
    .trim()
    .toLowerCase();

  // 1. Get all piping_spec_ table names
  const specTablesResponse = await secondAxios.post(`/sqlite-db/query`, {
    dbName: "piping",
    sql: `
      SELECT name
      FROM sqlite_master
      WHERE type = 'table'
        AND name GLOB 'piping_spec_*'
      ORDER BY name
    `,
  });

  // Adjust this depending on your backend response shape
  const specTableRows = Array.isArray(specTablesResponse.data)
    ? specTablesResponse.data
    : specTablesResponse.data?.data || [];

  const specTableKeys = specTableRows.map((row) => row.name);

  const allSpecData = [];

  // 2. Load data from every spec table
  for (const specKey of specTableKeys) {
    const safeTableName = specKey.replace(/"/g, '""');

    const tableDataResponse = await secondAxios.post(`/sqlite-db/query`, {
      dbName: "piping",
      sql: `SELECT * FROM "${safeTableName}"`,
    });

    const tableData = Array.isArray(tableDataResponse.data)
      ? tableDataResponse.data
      : tableDataResponse.data?.data || [];

    allSpecData.push(...tableData);
  }

  // 3. Catalogue rows only - merged with the project's own rows below.
  setDbSpecRows([...allSpecData]);
}, []);

  // Merge catalogue rows with the user-authored ones into the working spec
  // tables. Normalising here lets every reader use one column naming whichever
  // convention a row was saved in (see specRow.js).
  useEffect(() => {
    const merged = mergeSpecRowsByIdentity(
      [...dbSpecRows, ...customSpecRows],
      clientName || "DEMO",
    );

    setFullSpecTable(merged);
    setSpecTable(processSpecData(merged));
  }, [dbSpecRows, customSpecRows, clientName]);

  useEffect(() => {
    if (!selectedProjectId) return;

    async function fetchProject() {
      try {
        const base = await getProjectById(selectedProjectId);
        const nextClientName = String(base?.plant?.clientName || "DEMO").trim();

        // console.log("[InstrumentationContext] Project clientName:", nextClientName);
        setClientName(nextClientName);
      } catch (error) {
        console.error("Failed to fetch project clientName:", error);
        setClientName("DEMO");
      }
    }

    fetchProject();
  }, [selectedProjectId]);

  // Then load tables after clientName is available
  useEffect(() => {
    if (!selectedProjectId) return;
    if (!clientName) return;

    initializeAllTables(clientName);
  }, [selectedProjectId, clientName, initializeAllTables]);

  function addSlectedSpec(newSelc) {
    setSelectedSpecs((prev) => {
      const exists = prev.some((item) => item.label === newSelc.label);
      return exists ? prev : [...prev, newSelc];
    });
  }

  function removeSelectedSpec(toRemove) {
    setSelectedSpecs((prev) =>
      prev.filter((item) => item.label !== toRemove.label),
    );
  }

  async function getRevisionsFromPath(path) {
    if (!path) return [];

    const fn = window.electron?.freecad?.getRevisionsFromPath;
    if (typeof fn === "function") {
      const revs = await fn(path);
      return revs || [];
    }

    return [];
  }

  const getSelectedSpecTable = useCallback(
    (selc) => {
      if (!selc) return [];
      if (!specTable || !Array.isArray(specTable)) return [];

      return specTable.filter(
        (item) =>
          item.Spec === selc.spec &&
          item.Rev?.toString() === selc.rev?.toString(),
      );
    },
    [specTable],
  );

  // ─── specLibraryTable ───────────────────────────────────────────────────────
  // The raw spec rows for the currently selected specs. Size here is still a
  // *range* — buildSpecLibraryTable expands it into discrete sizes.
  const selectedSpecRows = useMemo(() => {
    if (!Array.isArray(selectedSpecs) || selectedSpecs.length === 0) return [];
    return selectedSpecs.flatMap(getSelectedSpecTable);
  }, [selectedSpecs, getSelectedSpecTable]);

  // Rebuild only when the actual spec/row set changes — selectedSpecRows is a
  // fresh array on every specTable render, so compare content, not identity.
  const specLibraryKey = useMemo(
    () =>
      selectedSpecRows
        .map(
          (r) =>
            `${r.Spec}|${r.Rev}|${r.Components}|${r.Size}|${r["Schedule/Rating"]}|${r.Ends}|${r.Material}`,
        )
        .join("~"),
    [selectedSpecRows],
  );

  const specLibraryRowsRef = useRef(selectedSpecRows);
  specLibraryRowsRef.current = selectedSpecRows;
  const fullSpecTableRef = useRef(fullSpecTable);
  fullSpecTableRef.current = fullSpecTable;

  useEffect(() => {
    let isCancelled = false;

    if (!specLibraryKey) {
      setSpecLibraryTable([]);
      setIsBuildingSpecLibrary(false);
      return undefined;
    }

    setIsBuildingSpecLibrary(true);

    buildSpecLibraryTable(specLibraryRowsRef.current, fullSpecTableRef.current)
      .then((rows) => {
        if (isCancelled) return;
        console.log(`[specLibraryTable] built ${rows.length} rows`);
        setSpecLibraryTable(rows);
      })
      .catch((error) => {
        if (isCancelled) return;
        console.error("[specLibraryTable] build failed:", error);
        setSpecLibraryTable([]);
      })
      .finally(() => {
        if (!isCancelled) setIsBuildingSpecLibrary(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [specLibraryKey]);


  // ─── Estimation revisions ───────────────────────────────────────────────────
  // Options come straight from the revision keys inside the loaded JSON.
  const estimationRevisions = useMemo(
    () =>
      Object.keys(estimationTable || {})
        .filter((key) => key !== "" && !isNaN(Number(key)))
        .sort((a, b) => Number(a) - Number(b)),
    [estimationTable],
  );

  // Confirming a revision is what actually surfaces its data — the tables read
  // selectedEstimationRevision, and nothing renders until one is set.
  const selectEstimationRevision = useCallback(
    (revisionKey) => {
      const key = String(revisionKey ?? "");

      setSelectedEstimationRevision(key);
      setConfirmedEstimationRevision(
        key ? { projectId: selectedProjectId, revision: key } : null,
      );

      if (!key) return;

      const revisionData = estimationTable?.[key] || {};
      if (revisionData.totalSummary) setTotalSummary(revisionData.totalSummary);
    },
    [estimationTable, selectedProjectId],
  );

  // Branch the current revision into a fresh one and switch to it.
  const addEstimationRevision = useCallback(() => {
    // A project with no saved estimation loads with a blank { 0: {} }
    // placeholder (see parseInstrumentationData). That empty Rev 0 is the first
    // revision still to be filled in, so open it rather than branching Rev 1.
    if (
      estimationRevisions.length === 1 &&
      estimationRevisions[0] === "0" &&
      Object.keys(estimationTable?.["0"] || {}).length === 0
    ) {
      setSelectedEstimationRevision("0");
      setConfirmedEstimationRevision({
        projectId: selectedProjectId,
        revision: "0",
      });
      return "0";
    }

    const numbers = estimationRevisions.map(Number).filter((n) => !isNaN(n));
    const nextRev = String(numbers.length ? Math.max(...numbers) + 1 : 0);

    // A new revision starts as a copy of the latest one in the WBS - not of
    // whichever revision happens to be selected, since opening from the tree
    // selects none and would otherwise start the new revision empty.
    setEstimationTable((prev) => {
      const latestRev = Object.keys(prev || {})
        .filter((key) => key !== "" && !isNaN(Number(key)))
        .sort((a, b) => Number(b) - Number(a))[0];
      return {
        ...prev,
        [nextRev]: JSON.parse(JSON.stringify(prev?.[latestRev] || {})),
      };
    });

    setSelectedEstimationRevision(nextRev);
    setConfirmedEstimationRevision({
      projectId: selectedProjectId,
      revision: nextRev,
    });

    return nextRev;
  }, [estimationRevisions, estimationTable, selectedProjectId]);

  async function loadInstrumentationData(projectIdValue) {
    setLoadedProjectId(null);
    const revisions = await fetchAllRevsForProject(projectIdValue);
    let rev;

    if (!revisions.length) {
      rev = await postInstrumentationRev(projectIdValue, {
        name: "0",
        data: "{}",
      });
    } else {
      rev = revisions[revisions.length - 1];
    }

    setRevision(rev);
    const jsonData = await fetchInstrumentationRev(projectIdValue, rev.id);
    parseInstrumentationData(jsonData);
    await linkPlantSpec(projectIdValue);
    // Marks the point where estimationTable/estimationRevisions describe THIS
    // project - deep links from the EMS tree wait on it before selecting (or
    // branching) a revision, since parseInstrumentationData resets the selection.
    setLoadedProjectId(String(projectIdValue));
  }

  /**
   * An Estimation WBS keeps no specs of its own: they are read from the plant's
   * Spec record and applied over what the load just parsed. Overwriting, not
   * filling gaps - a save writes the specs back as a snapshot, so a gap-fill
   * would find that snapshot on the next load and never see later edits made on
   * the Spec record. Same rule as the Software's resolveLinkedSources.
   *
   * With no Spec record, or several, nothing is linked and the snapshot stays.
   */
  const loadIdRef = useRef(null);

  async function linkPlantSpec(projectIdValue) {
    const loadId = String(projectIdValue);
    loadIdRef.current = loadId;

    try {
      // An Instrumentation WBS is estimation-only and never owns specs, so it
      // always reads them from the plant's Spec record.
      const base = await getProjectById(projectIdValue);

      const { data, candidates } = await resolvePlantSpec(
        base?.plant?.id,
        String(base?.plant?.clientName || "DEMO").trim(),
      );
      // A slower lookup for a project already switched away from must not
      // overwrite the specs of the one now open.
      if (loadIdRef.current !== loadId) return;

      if (!data) {
        console.warn(
          candidates.length
            ? `[InstrumentationContext] Plant has ${candidates.length} Spec records; not linking any.`
            : "[InstrumentationContext] Plant has no Spec record to link specs from.",
        );
        return;
      }

      setSelectedSpecs(data.selectedSpecs);
      setCustomSpecRows(
        mergeSpecRowsByIdentity(data.customSpecRows, base?.plant?.clientName),
      );
      setSpecHelperConfigs(data.specHelperConfigs);
    } catch (error) {
      console.error("[InstrumentationContext] Failed to link the plant's Spec:", error);
    }
  }

  function parseInstrumentationData(raw) {
    // Not raw.data directly: revisions saved by the Software also come back
    // JSON-stringified or wrapped once more, and reading those as raw.data
    // yields nothing - the Spec page then loads blank.
    const data = extractInstrumentationRevisionData(raw);

    setProjectPath(data.projectPath || null);
    setSelectedSpecs(data.selectedSpecs || []);
    setCustomSpecRows(
      mergeSpecRowsByIdentity(
        Array.isArray(data.customSpecRows) ? data.customSpecRows : [],
        clientName || "DEMO",
      ),
    );
    setSpecHelperConfigs(
      data.specHelperConfigs && typeof data.specHelperConfigs === "object"
        ? data.specHelperConfigs
        : {},
    );
    // setAllCombinationTable(data.allCombinationTable || []);
    setTagNumbersList(migrateTagNumbersList(data.tagNumbersList));
    setSelectedLineNumber(String(data.selectedLineNumber ?? "").trim());
    setPdcAttachItems(data.pdcAttachItems || []);
    setEstimationTable(data.estimationTable || { 0: {} });

    // Deliberately NOT auto-selecting a revision. The dropdown gets its
    // options from estimationTable, but nothing is shown until the user
    // confirms a revision, so switching projects never surfaces stale data.
    setSelectedEstimationRevision("");
    setConfirmedEstimationRevision(null);
    // setUID_array(Array.isArray(data.UID_array) ? data.UID_array : []);
    setPartLibTable(() => {
      if (!data.partLibTable || Object.keys(data.partLibTable).length === 0) {
        return { 0: {} };
      }
      return data.partLibTable;
    });

    const normalizedPnidData = buildNormalizedPnidData(data);
    setPnidData(normalizedPnidData);

    const pnidRevisionKeys = Object.keys(normalizedPnidData)
      .filter((key) => !key.startsWith("__"))
      .sort((a, b) => Number(a) - Number(b));

    const savedPnidRevision = String(data.selectedPnidRevision || "").trim();
    const fallbackPnidRevision = pnidRevisionKeys.at(-1) || "";

    setSelectedPnidRevision(
      savedPnidRevision && normalizedPnidData[savedPnidRevision]
        ? savedPnidRevision
        : fallbackPnidRevision,
    );

    setPnidProjectPath(data.pnidProjectPath || "");
    setPnidRevisionFolders(data.pnidRevisionFolders || []);
    setPnidFolderTree(data.pnidFolderTree || null);

    const newModelingData = (() => {
      if (!data?.modelingData || Object.keys(data?.modelingData).length === 0) {
        if (
          !data?.modelingTable ||
          Object.keys(data?.modelingTable).length === 0
        ) {
          return { 0: {} };
        }
        return data.modelingTable;
      }
      return data.modelingData;
    })();

    setModelingData(newModelingData);
    setHookupsTable(data.hookupsTable || []);

    const validKeys = Object.keys(newModelingData)
      .filter((key) => !key.startsWith("__") && !Number.isNaN(Number(key)))
      .sort((a, b) => Number(a) - Number(b));

    const lastKey = validKeys.at(-1) ?? "0";
    setSelectedModelingRevision(lastKey);
  }
  function norm(value) {
    return String(value ?? "").trim();
  }

  function getPipeCombosForRow(row, allCombinationTable) {
    const spec = norm(row?.Spec);
    const size = norm(row?.Size);
    const sch = norm(row?.["Schedule/Rating"]);

    const source = Array.isArray(allCombinationTable)
      ? allCombinationTable
      : [];

    const combosMap = new Map();

    for (const r of source) {
      const rSpec = norm(r?.Spec);
      const rComp = norm(r?.Components ?? r?.Component);
      const rSize = norm(r?.Size);
      const rSch = norm(r?.["Schedule/Rating"]);
      const rEnds = norm(r?.Ends);
      const rMaterial = norm(r?.Material);

      if (rSpec !== spec) continue;
      if (rComp.toLowerCase() !== "pipe") continue;
      if (rSize !== size) continue;
      if (sch && rSch && rSch !== sch) continue;

      const key = `${rEnds}||${rMaterial}`;
      if (!combosMap.has(key)) {
        combosMap.set(key, {
          Ends: rEnds,
          Material: rMaterial,
        });
      }
    }

    return [...combosMap.values()];
  }

  function autoFillPipeRow(row, allCombinationTable) {
    if (norm(row?.Component).toLowerCase() !== "pipe") return row;

    const combos = getPipeCombosForRow(row, allCombinationTable);
    if (combos.length === 0) return row;

    const currentEnds = norm(row?.Ends);
    const currentMaterial = norm(row?.Material);

    const nextEnds = currentEnds || combos[0].Ends || "";
    const materialCandidates = combos
      .filter((c) => !nextEnds || c.Ends === nextEnds)
      .map((c) => c.Material)
      .filter(Boolean);

    const nextMaterial =
      currentMaterial || materialCandidates[0] || combos[0].Material || "";

    return {
      ...row,
      Ends: nextEnds,
      Material: nextMaterial,
    };
  }
  function buildInstrumentationPayload() {
    const normalizedPnidData = Object.fromEntries(
      Object.entries(isPlainObject(pnidData) ? pnidData : {}).map(
        ([revisionKey, revisionValue]) => [
          String(revisionKey),
          normalizePnidRevisionData(revisionValue),
        ],
      ),
    );

    return {
      name: selectedProjectId,
      type: INSTRUMENTATION_TYPE,
      revision: revision?.id,
      data: {
        selectedSpecs,
        // Both written by the Software's Spec Helper. The payload replaces the
        // saved blob wholesale, so leaving them out would delete them.
        customSpecRows,
        specHelperConfigs,
        // specTable,
        partLibTable,
        SelectedParts,
        pdcAttachItems,
        modelingData,
        hookupsTable,
        projectPath,
        // allCombinationTable,
        tagNumbersList,
        selectedLineNumber,
        estimationTable,
        pnidData: normalizedPnidData,
        pnidProjectPath,
        pnidRevisionFolders,
        pnidFolderTree,
        selectedPnidRevision,
        UID_array,
      },
    };
  }

  async function saveInstrumentationData() {
    const data = buildInstrumentationPayload();
    console.log("saving", data);

    if (revision?.id) {
      await updateInstrumentationRev(selectedProjectId, revision.id, data);
    } else {
      const rev = await postInstrumentationRev(selectedProjectId, {
        name: "0",
        data: data?.data || {},
      });
      setRevision(rev);
    }
    notifyProjectSaved(selectedProjectId);
  }


  // Load project data first
  useEffect(() => {
    if (!selectedProjectId) return;
    loadInstrumentationData(selectedProjectId);
  }, [selectedProjectId]);
  function cleanLineValue(value) {
    return String(value ?? "").trim();
  }

  function getLineField(row, keys) {
    for (const key of keys) {
      const value = cleanLineValue(row?.[key]);
      if (value) return value;
    }
    return "";
  }

  function normalizeLinePipeSize(value) {
    return cleanLineValue(value).replace(/"/g, "");
  }

  function buildPipeBulkRowsFromLineData(lineRows, pnidPath) {
    const rows = Array.isArray(lineRows) ? lineRows : [];
    const seen = new Set();
    const out = [];

    for (const item of rows) {
      const lineNumber = getLineField(item, ["Line Number", "LineNumber"]);
      const spec = getLineField(item, [
        "Pipe Classification",
        "PipeClassification",
      ]);
      const size = normalizeLinePipeSize(
        getLineField(item, ["Nominal Pipe Size", "NominalPipeSize"]),
      );
      const scheduleRating = getLineField(item, [
        "Schedule/Rating",
        "ScheduleRating",
      ]);

      if (!spec || !size) continue;

      const dedupeKey = [pnidPath, lineNumber, spec, size, scheduleRating].join(
        "|",
      );

      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);

      out.push({
        _rowId: `line-pipe-${dedupeKey}`,
        _derived: true,
        _derivedFrom: "lineList",
        path: pnidPath,
        sourceLineNumber: lineNumber,
        "Line Number": lineNumber,
        Spec: spec,
        Component: "Pipe",
        Size: size,
        "Schedule/Rating": scheduleRating,
        Ends: "",
        Material: "",
        Qty: 1,
        Category: "Bulk",
      });

      out.push({
        _rowId: `line-elbow-${dedupeKey}`,
        _derived: true,
        _derivedFrom: "lineList",
        path: pnidPath,
        sourceLineNumber: lineNumber,
        "Line Number": lineNumber,
        Spec: spec,
        Component: "Elbow 90",
        Size: size,
        "Schedule/Rating": scheduleRating,
        Ends: "",
        Material: "",
        Qty: 1,
        Category: "Bulk",
      });
    }

    return out;
  }
  async function extractPnid(pnidPath) {
    if (!selectedPnidRevision) {
      console.warn("No selected P&ID revision");
      return;
    }

    setIsFetchingPnid(true);

    try {
      const { TaggedData, LineData } = await fetchExtractPnid(pnidPath);
      console.log("Fetched P&ID table:", TaggedData);

      const valveRowsRaw = Array.isArray(TaggedData.ValveData)
        ? TaggedData.ValveData
        : [];
      const instrumentRowsRaw = Array.isArray(TaggedData.InstrumentData)
        ? TaggedData.InstrumentData
        : [];

      const valveRowsWithSheets = valveRowsRaw.map((r) => ({
        ...r,
        dataSheet:
          buildDataSheetForSourceRow(r, "valve", allTables) ??
          r?.dataSheet ??
          null,
      }));
      const instrumentRowsWithSheets = instrumentRowsRaw.map((r) => ({
        ...r,
        dataSheet:
          buildDataSheetForSourceRow(r, "instrument", allTables) ??
          r?.dataSheet ??
          null,
      }));

      const equipmentRowsRaw = Array.isArray(TaggedData.EquipmentData)
        ? TaggedData.EquipmentData
        : [];
      const equipmentRowsWithSheets = equipmentRowsRaw.map((r) => ({
        ...r,
        dataSheet:
          buildDataSheetForSourceRow(r, "equipment", allTables) ??
          r?.dataSheet ??
          null,
      }));

      setInstrumentIndexTable(instrumentRowsWithSheets);
      setValveListTable(valveRowsWithSheets);

      const extractedLineRows = Array.isArray(LineData?.LineData)
        ? LineData.LineData
        : [];


      const rawBulkRows = Array.isArray(TaggedData?.BulkData)
        ? TaggedData.BulkData
        : [];

      // Pipe and Elbow should come only from line list
      const bulkWithoutPipeAndElbow = rawBulkRows.filter((row) => {
        const comp = String(row?.Component || "").trim().toLowerCase();
        return comp !== "pipe" && comp !== "elbow" && comp !== "elbow 90";
      });

      const lineDerivedRows = buildPipeBulkRowsFromLineData(
        extractedLineRows,
        pnidPath,
      );

      setBulkListTable((prev) => {
        const prevRows = Array.isArray(prev) ? prev : [];

        // remove old Pipe / Elbow rows for the same drawing path
        const cleanedPrev = prevRows.filter((row) => {
          const samePath = String(row?.path || "") === String(pnidPath);
          const comp = String(row?.Component || "").trim().toLowerCase();
          const isPipeOrElbow =
            comp === "pipe" || comp === "elbow" || comp === "elbow 90";

          return !(samePath && isPipeOrElbow);
        });

        // normal bulk rows still use derivation helper
        const mergedBase = mergeBulkDataWithDerivation(
          cleanedPrev,
          bulkWithoutPipeAndElbow,
          allTables,
        );

        // line-derived Pipe / Elbow rows merge directly
        return mergeBulkData(mergedBase, lineDerivedRows);
      });


      setTitleBlockData(TaggedData.TitleBlock || {});

      const trimRowsWithSheets = (TaggedData.Trimlist || []).map((r) => ({
        ...r,
        dataSheet:
          buildRowDataSheet(
            r,
            valveRowsWithSheets,
            instrumentRowsWithSheets,
            allTables,
            equipmentRowsWithSheets,
          ) ??
          r?.dataSheet ??
          null,
      }));
      setTrimListTable((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        ...trimRowsWithSheets,
      ]);

      setEquipmentListTable((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        ...equipmentRowsWithSheets,
      ]);

      const mergeConsolidated = (prev, next) => {
        const prevItems = Array.isArray(prev?.items) ? prev.items : [];
        const nextItems = Array.isArray(next?.items) ? next.items : [];
        const items = [...prevItems, ...nextItems];
        return {
          tab: next?.tab ?? prev?.tab,
          totalSourceRows:
            (prev?.totalSourceRows || 0) + (next?.totalSourceRows || 0),
          totalGroupedSheets: items.length,
          items,
        };
      };

      setDataSheetsStore((prev) => ({
        ...prev,
        valve: mergeConsolidated(
          prev?.valve,
          consolidateDataSheets(valveRowsWithSheets, "valve", allTables),
        ),
        instrument: mergeConsolidated(
          prev?.instrument,
          consolidateDataSheets(
            instrumentRowsWithSheets,
            "instrument",
            allTables,
          ),
        ),
        equipment: mergeConsolidated(
          prev?.equipment,
          consolidateDataSheets(
            equipmentRowsWithSheets,
            "equipment",
            allTables,
          ),
        ),
      }));

      if (LineData && LineData.LineData && Array.isArray(LineData.LineData)) {
        const mappedLineData = LineData.LineData.map((item) => ({
          path: pnidPath,
          ...item,
        }));
        setLineListTable((prev) => {
          const prevRows = Array.isArray(prev) ? prev : [];
          const cleanedPrev = prevRows.filter(
            (row) => String(row?.path || "") !== String(pnidPath),
          );
          return [...cleanedPrev, ...mappedLineData];
        });
        // setLineListTable((prev) => [...prev, ...mappedLineData]);
        // console.log("Mapped Line Data:", mappedLineData);
      }
    } catch (e) {
      console.log("Error while fetching pnid: ", e);
    } finally {
      setIsFetchingPnid(false);
    }
  }

  return (
    <InstrumentationContext.Provider
      value={{
        selectedProjectId,
        setSelectedProjectId,
        specTable,
        fullSpecTable,
        customSpecRows,
        setCustomSpecRows,
        specHelperConfigs,
        allTables,
        initializeAllTables,
        selectedSpecs,
        setSelectedSpecs,
        SelectedParts,
        addSlectedSpec,
        removeSelectedSpec,
        getSelectedSpecTable,
        saveInstrumentationData,
        partLibTable,
        setPartLibTable,
        estimationTable,
        setEstimationTable,
        selectedEstimationRevision,
        confirmedEstimationRevision,
        estimationRevisions,
        selectEstimationRevision,
        addEstimationRevision,
        pnidData,
        setPnidData,
        selectedPnidRevision,
        setSelectedPnidRevision,
        pnidProjectPath,
        setPnidProjectPath,
        pnidRevisionFolders,
        setPnidRevisionFolders,
        pnidFolderTree,
        setPnidFolderTree,
        initializeRevisionIfNeeded,
        clearSelectedPnidRevision,
        trimListTable,
        setTrimListTable,
        bulkListTable,
        setBulkListTable,
        valveListTable,
        setValveListTable,
        instrumentIndexTable,
        setInstrumentIndexTable,
        lineListTable,
        setLineListTable,
        equipmentListTable,
        setEquipmentListTable,
        dataSheetsStore,
        setDataSheetsStore,
        titleBlockData,
        setTitleBlockData,
        extractPnid,
        isFetchingPnid,
        pnidDrawings,
        setPnidDrawings,
        modelingData,
        setModelingData,
        pdcAttachItems,
        setPdcAttachItems,
        allUiData,
        pageViews,
        setPageViews,
        pages,
        setPages,
        selectedModelingRevision,
        setSelectedModelingRevision,
        projectPath,
        setProjectPath,
        hookupsTable,
        setHookupsTable,
        getRevisionsFromPath,
        fastenersTable,
        setFastenersTable,
        totalSummary,
        setTotalSummary,
        tagNumbersList,
        setTagNumbersList,
        selectedLineNumber,
        setSelectedLineNumber,
        allCombinationTable,
        setAllCombinationTable,
        specLibraryTable,
        setSpecLibraryTable,
        isBuildingSpecLibrary,
        loadInstrumentationData,
        loadedProjectId,
        validationObject,
        setValidationObject,
        clientName,
        UID_array,
        setUID_array,
      }}
    >
      {children}
    </InstrumentationContext.Provider>
  );
};

export const useInstrumentation = () => {
  const context = useContext(InstrumentationContext);
  if (!context) {
    throw new Error("useInstrumentation must be used within a InstrumentationProvider");
  }
  return context;
};
